import React from 'react';
import { JobStatusBadge } from './JobStatusBadge';
import { useSignedUrl } from '../../hooks/useSignedUrl';

/**
 * Reusable job list item component
 * Displays job thumbnail, status, and timestamp
 *
 * Thumbnail loading:
 * - Uses lazy URL signing for fast initial render
 * - Shows skeleton placeholder while signed URL is being fetched
 * - Handles both gs:// URLs (needs signing) and http URLs (already signed)
 */
export const JobListItem = ({ job, onClick }) => {
  // Lazy load signed URL for thumbnail
  const { signedUrl, loading: urlLoading } = useSignedUrl(job.image_url);

  return (
    <div
      onClick={() => onClick(job)}
      className="px-4 sm:px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
    >
      <div className="flex items-center gap-3 sm:gap-4">
        {/* Left: Thumbnail with lazy loading */}
        <div className="flex-shrink-0">
          {signedUrl ? (
            <img
              src={signedUrl}
              alt="Upload"
              className="h-16 w-16 sm:h-20 sm:w-20 rounded-lg object-cover ring-1 ring-gray-200"
            />
          ) : (
            // Skeleton placeholder while URL is being signed
            <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-lg bg-gray-200 animate-pulse ring-1 ring-gray-200" />
          )}
        </div>

        {/* Right: Status and Timestamp */}
        <div className="flex-1 min-w-0 flex flex-col items-end justify-center gap-2">
          {/* Status Badge */}
          <JobStatusBadge status={job.status} />

          {/* Timestamp */}
          <p className="text-xs sm:text-sm text-gray-500 text-right">
            {new Date(job.created_at).toLocaleString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit'
            })}
          </p>
        </div>
      </div>
    </div>
  );
};
